/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import {
  Alert, Button, Card, DatePicker, Drawer, Empty, Form, InputNumber, Input, Modal, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import i18n, { datePickerFormat, formatDate } from '../../i18n';
import {
  useGetInventoryLotsQuery,
  useGetInventoryTransactionsQuery,
  useGetInventoryReservationsQuery,
  useWithdrawInventoryMutation,
  useReserveInventoryMutation,
  useCorrectInventoryMutation,
  useReleaseReservationMutation,
} from '../../api/inventoryApi';
import type { InventoryLot } from '../../types/inventory';

const { Text } = Typography;

const fmt = (v: string | null | undefined): string => {
  if (v == null) return '—';
  const n = Number(v);
  return Number.isFinite(n) ? i18n.t('inventory:units.kgValue', { value: Number(n.toFixed(3)) }) : '—';
};

/** Label of a lot origin / transaction code (`inventory:ledger.origin.*` / `inventory:ledger.tx.*`), or the code itself. */
const codeLabel = (group: 'origin' | 'tx', code: string): string => {
  const key = `inventory:ledger.${group}.${code}`;
  return i18n.exists(key) ? i18n.t(key) : code;
};

type ActionKind = 'withdraw' | 'reserve' | 'correct';

const DATE_FMT = 'YYYY-MM-DD';

// Складская операция датируется сим-днём в окне [дата сбора лота, курсор]
// (RFC_HARVEST_ECONOMY §7): раньше даты — лота ещё не было, позже курсора —
// откат курсора не сможет её снять.
const ledgerErrorText = (code: string | undefined): string | null => {
  if (!code) return null;
  const key = `inventory:ledger.errors.${code}`;
  return i18n.exists(key) ? i18n.t(key) : null;
};
const ledgerError = (e: any, fallback: string): string =>
  ledgerErrorText(e?.data?.code as string) || e?.data?.message || fallback;

interface Props {
  saveId: string;
  /** Тип сейва: складские команды принимает только `free`. */
  saveType?: string | null;
  /** Курсор сейва (сим-дата YYYY-MM-DD) — дата операции по умолчанию и верхняя граница окна. */
  cursorDate?: string | null;
}

const InventoryLedgerSection: React.FC<Props> = ({ saveId, saveType = null, cursorDate = null }) => {
  const { t } = useTranslation('inventory');
  const { data: lots = [], isLoading } = useGetInventoryLotsQuery({ saveId });
  const { data: openReservations = [] } = useGetInventoryReservationsQuery({ saveId, status: 'open' });
  const [withdraw, { isLoading: withdrawing }] = useWithdrawInventoryMutation();
  const [reserve, { isLoading: reserving }] = useReserveInventoryMutation();
  const [correct, { isLoading: correcting }] = useCorrectInventoryMutation();
  const [releaseReservation] = useReleaseReservationMutation();

  const ledgerWritable = saveType == null || saveType === 'free';
  const cursorDay = cursorDate ? dayjs(cursorDate) : null;

  const reservationsByLot = new Map<string, typeof openReservations>();
  for (const r of openReservations) {
    const list = reservationsByLot.get(r.lot_id) || [];
    list.push(r);
    reservationsByLot.set(r.lot_id, list);
  }

  const doRelease = async (reservationId: string, lotId: string) => {
    if (!cursorDay) {
      message.error(t('ledger.errors.LEDGER_DATE_REQUIRED'));
      return;
    }
    try {
      // Снятие резерва датируется курсором: это «сегодня» сейва, верхняя граница окна.
      await releaseReservation({ saveId, reservationId, lotId, payload: { effectiveDate: cursorDay.format(DATE_FMT) } }).unwrap();
      message.success(t('liveAssets.released'));
    } catch (e: any) {
      message.error(ledgerError(e, t('ledger.releaseFailed')));
    }
  };

  const [action, setAction] = useState<{ kind: ActionKind; lot: InventoryLot } | null>(null);
  const [txLot, setTxLot] = useState<InventoryLot | null>(null);
  const [form] = Form.useForm();

  const openAction = (kind: ActionKind, lot: InventoryLot) => {
    form.resetFields();
    form.setFieldsValue({ effectiveDate: cursorDay });
    setAction({ kind, lot });
  };

  const disabledDateFor = (lot: InventoryLot) => (d: Dayjs) => {
    const origin = lot.origin_date ? dayjs(lot.origin_date) : null;
    if (origin && d.isBefore(origin, 'day')) return true;
    if (cursorDay && d.isAfter(cursorDay, 'day')) return true;
    return false;
  };

  const submit = async () => {
    if (!action) return;
    try {
      const v = await form.validateFields();
      const { kind, lot } = action;
      const effectiveDate = (v.effectiveDate as Dayjs).format(DATE_FMT);
      if (kind === 'withdraw') {
        await withdraw({ saveId, lotId: lot.id, payload: { quantity: v.quantity, reason: v.reason, effectiveDate, counterpartyRef: v.counterpartyRef, notes: v.notes } }).unwrap();
        message.success(t('ledger.withdrawn'));
      } else if (kind === 'reserve') {
        await reserve({ saveId, lotId: lot.id, payload: { quantity: v.quantity, effectiveDate, reason: v.reason } }).unwrap();
        message.success(t('liveAssets.reserved'));
      } else {
        await correct({ saveId, lotId: lot.id, payload: { quantity: v.quantity, direction: v.direction, effectiveDate, reason: v.reason } }).unwrap();
        message.success(t('ledger.corrected'));
      }
      setAction(null);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(ledgerError(e, t('ledger.operationFailed')));
    }
  };

  const columns = [
    {
      title: t('ledger.product'),
      key: 'product',
      render: (_: unknown, lot: InventoryLot) => (
        <Space direction="vertical" size={0}>
          <Text strong>{lot.produce_type_name || lot.crop_name || t('ledger.produceFallback')}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            <Tag>{codeLabel('origin', lot.origin_type)}</Tag>{lot.origin_date ? formatDate(lot.origin_date, 'short') : ''}
          </Text>
        </Space>
      ),
    },
    { title: t('ledger.onHand'), key: 'on_hand', align: 'right' as const, render: (_: unknown, l: InventoryLot) => fmt(l.quantity_on_hand) },
    { title: t('liveAssets.reserve'), key: 'reserved', align: 'right' as const, render: (_: unknown, l: InventoryLot) => fmt(l.quantity_reserved) },
    {
      title: t('liveAssets.available'), key: 'available', align: 'right' as const,
      render: (_: unknown, l: InventoryLot) => <Text strong>{fmt(l.quantity_available ?? l.quantity_on_hand)}</Text>,
    },
    {
      title: t('common:labels.actions'), key: 'actions',
      render: (_: unknown, lot: InventoryLot) => {
        const resv = reservationsByLot.get(lot.id) || [];
        return (
          <Space direction="vertical" size={2}>
            <Space wrap size={4}>
              <Button size="small" type="primary" disabled={!ledgerWritable} onClick={() => openAction('withdraw', lot)}>{t('ledger.withdraw')}</Button>
              <Button size="small" disabled={!ledgerWritable} onClick={() => openAction('reserve', lot)}>{t('liveAssets.reserve')}</Button>
              <Button size="small" disabled={!ledgerWritable} onClick={() => openAction('correct', lot)}>{t('ledger.correction')}</Button>
              <Button size="small" type="link" onClick={() => setTxLot(lot)}>{t('ledger.history')}</Button>
            </Space>
            {resv.length > 0 && (
              <Space wrap size={4}>
                {resv.map((r) => (
                  <Tag key={r.id} color="gold" closable={ledgerWritable} closeIcon={t('liveAssets.release')}
                    onClose={(e) => { e.preventDefault(); doRelease(r.id, lot.id); }}>
                    {t('ledger.reserveTag', { value: fmt(r.quantity) })}
                  </Tag>
                ))}
              </Space>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <Card title={t('ledger.title')} loading={isLoading}>
      {!ledgerWritable && (
        <Alert type="info" showIcon style={{ marginBottom: 12 }}
          message={t('ledger.readOnlyHint')} />
      )}
      {lots.length === 0 ? (
        <Empty description={t('ledger.empty')} />
      ) : (
        <Table
          rowKey="id"
          size="small"
          dataSource={lots}
          columns={columns}
          pagination={false}
        />
      )}

      <Modal
        open={!!action}
        title={action?.kind === 'withdraw' ? t('ledger.withdrawTitle') : action?.kind === 'reserve' ? t('ledger.reserveTitle') : t('ledger.correctTitle')}
        onOk={submit}
        onCancel={() => setAction(null)}
        confirmLoading={withdrawing || reserving || correcting}
        okText={action?.kind === 'withdraw' ? t('ledger.withdraw') : action?.kind === 'reserve' ? t('liveAssets.doReserve') : t('common:actions.apply')}
        cancelText={t('common:actions.cancel')}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="effectiveDate"
            label={t('ledger.operationDate')}
            extra={action ? t('ledger.window', {
              from: action.lot.origin_date ? formatDate(action.lot.origin_date, 'short') : '—',
              to: cursorDate ? formatDate(cursorDate, 'short') : t('ledger.currentSaveDay'),
            }) : undefined}
            rules={[{ required: true, message: t('ledger.errors.LEDGER_DATE_REQUIRED') }]}
          >
            <DatePicker
              style={{ width: '100%' }}
              format={datePickerFormat()}
              allowClear={false}
              disabledDate={action ? disabledDateFor(action.lot) : undefined}
              // Пикер якорится на курсор сейва, не на реальное «сегодня» (см. SaveDetailPage).
              defaultPickerValue={cursorDay ?? undefined}
            />
          </Form.Item>
          <Form.Item name="quantity" label={t('ledger.quantityKg')} rules={[{ required: true, message: t('liveAssets.quantityRequired') }, { type: 'number', min: 0.001, message: t('ledger.positive') }]}>
            <InputNumber min={0.001} step={0.1} style={{ width: '100%' }} />
          </Form.Item>
          {action?.kind === 'withdraw' && (
            <>
              <Form.Item name="reason" label={t('ledger.reason')} initialValue="sale" rules={[{ required: true }]}>
                <Select options={(['sale', 'gift', 'consumption', 'waste', 'scenario'] as const).map((value) => ({
                  value, label: t(`ledger.withdrawReason.${value}`),
                }))} />
              </Form.Item>
              <Form.Item name="counterpartyRef" label={t('liveAssets.counterpartyOptional')}><Input /></Form.Item>
              <Form.Item name="notes" label={t('ledger.notesOptional')}><Input.TextArea rows={2} /></Form.Item>
            </>
          )}
          {action?.kind === 'reserve' && (
            <Form.Item name="reason" label={t('liveAssets.reasonOptional')}><Input placeholder={t('liveAssets.reasonPlaceholder')} /></Form.Item>
          )}
          {action?.kind === 'correct' && (
            <>
              <Form.Item name="direction" label={t('ledger.direction')} initialValue="out" rules={[{ required: true }]}>
                <Select options={[{ value: 'in', label: t('ledger.directionIn') }, { value: 'out', label: t('ledger.directionOut') }]} />
              </Form.Item>
              <Form.Item name="reason" label={t('liveAssets.reasonOptional')}><Input /></Form.Item>
            </>
          )}
        </Form>
      </Modal>

      <Drawer
        open={!!txLot}
        width={520}
        title={t('ledger.lotHistory', { name: txLot?.produce_type_name || txLot?.crop_name || '' })}
        onClose={() => setTxLot(null)}
        destroyOnClose
      >
        {txLot && <LotTransactions saveId={saveId} lotId={txLot.id} />}
      </Drawer>
    </Card>
  );
};

const LotTransactions: React.FC<{ saveId: string; lotId: string }> = ({ saveId, lotId }) => {
  const { t } = useTranslation('inventory');
  const { data: txs = [], isLoading } = useGetInventoryTransactionsQuery({ saveId, lotId });
  return (
    <Table
      rowKey="id"
      size="small"
      loading={isLoading}
      dataSource={txs}
      pagination={false}
      columns={[
        { title: t('common:labels.date'), dataIndex: 'effective_date', key: 'date', render: (d: string | null) => (d ? formatDate(d, 'short') : '—') },
        { title: t('ledger.operation'), key: 'type', render: (_: unknown, tx) => <Tag>{codeLabel('tx', tx.transaction_type)}</Tag> },
        { title: t('ledger.quantityKgShort'), dataIndex: 'quantity', key: 'qty', align: 'right' as const, render: (q: string) => Number(Number(q).toFixed(3)) },
        { title: t('ledger.reason'), dataIndex: 'reason', key: 'reason', render: (r: string | null) => r || '—' },
      ]}
    />
  );
};

export default InventoryLedgerSection;
